import { SignOut } from "@repo/api/client";
import { Avatar, AvatarFallback, AvatarImage } from "@repo/ui/components/base/avatar";
import { Button } from "@repo/ui/components/base/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@repo/ui/components/base/dropdown-menu";
import { cn } from "@repo/ui/lib/utils";
import { usePathname, useRouter } from "next/navigation";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import { CiDark as DarkIcon, CiLight as LightIcon } from "react-icons/ci";
import { IoMdLogIn as SignInIcon } from "react-icons/io";
import { IoMenu as MenuIcon } from "react-icons/io5";
import { HoverBorderGradient } from "@/components/decor/HoverBorderGradient";
import { useStore } from "@/store";

export const AppHeader = () => {
  const router = useRouter();
  const { setTheme } = useTheme();
  const pathname = usePathname();

  const user = useStore((state) => state.user);
  const PageContextHeader = useStore((state) => state.pageContextHeader);
  const setIsMenuOpen = useStore((state) => state.setIsMenuOpen);
  const [isHeaderCompact, setIsHeaderCompact] = useState(false);

  useEffect(() => {
    function handleScroll() {
      const verticalScrollPosition = document.querySelector("html")?.scrollTop;
      if (verticalScrollPosition) {
        setIsHeaderCompact(verticalScrollPosition > 15 || pathname !== "/");
      }
    }

    // first evaluation in case page scroll not initialized from top
    handleScroll();

    document.addEventListener("scroll", handleScroll);

    return function cleanUp() {
      document.removeEventListener("scroll", handleScroll);
    };
  }, [isHeaderCompact, pathname]);

  return (
    <header className="z-[5] header text-foreground sticky top-0 left-0 right-0 flex items-center justify-center">
      <div
        className={cn("transition-all dark:backdrop-blur border-[1px] flex items-center justify-between", {
          "bg-popover/50 border-solid border-transparent w-full h-full": !isHeaderCompact,
          "border-solid border-disabled compact-header rounded-3xl bg-popover/80": isHeaderCompact,
        })}
      />

      <div className="absolute flex items-center justify-between px-[6px] -translate-x-1/2 -translate-y-1/2 compact-header top-1/2 left-1/2 ">
        <div className="">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <div className="flex items-center">
                  <Avatar className="border-[1px] border-secondary size-9">
                    <AvatarImage src={user.profilePictureUrl} />
                    <AvatarFallback>
                      {user.displayName?.[0]?.toUpperCase() ?? user.email[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="bottom" align="start" sideOffset={12}>
                <DropdownMenuLabel>Workbench</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    router.push("/settings");
                  }}
                >
                  settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={SignOut}>Logout</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <HoverBorderGradient
              as="button"
              className="rounded-full flex items-center space-x-2 dark:bg-24dp dark:text-foreground bg-secondary text-secondary-foreground py-1.5"
              onClick={() => router.push("/auth")}
            >
              <SignInIcon className="size-6" />
              <span>Sign in</span>
            </HoverBorderGradient>
          )}
          {PageContextHeader && (
            <div className="flex items-center">
              <PageContextHeader />
            </div>
          )}
        </div>
        {/* <Header.TertiaryItem>Hello 3</Header.TertiaryItem> */}
        <div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="rounded-full" size="icon" variant="ghost">
                  <LightIcon className="w-6 h-6 transition-all scale-100 rotate-0 dark:-rotate-90 dark:scale-0" />
                  <DarkIcon className="absolute w-6 h-6 transition-all scale-0 rotate-90 dark:rotate-0 dark:scale-100" />
                  <span className="sr-only">Toggle theme</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setTheme("light")}>Light</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme("dark")}>Dark</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTheme("system")}>System</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button variant="ghost" size="icon" className="rounded-full" onClick={() => setIsMenuOpen(true)}>
              <MenuIcon className="w-6 h-6" />
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};
